import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams
} from "react-router-dom";
import {
  Save,
  Briefcase
} from "lucide-react";
import * as ownerApi from "../../api/payroll/roleSalaryApi";
import * as portalApi from "../../api/payroll/roleSalaryPortalApi";
import {
  getRoles
} from "../../api/roleApi"; // Adjust import path if needed
import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  Checkbox
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function RoleSalaryForm({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/role-salaries" : "/role-salaries";
  const navigate = useNavigate();
  const { id } = useParams();

  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [designation, setDesignation] = useState("");
  const [fixedSalary, setFixedSalary] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await getRoles();
        // Extract array handling API response wrappers
        const rolesList = Array.isArray(res)
          ? res
          : res?.data || res?.roles || [];
        setRoles(rolesList);
      } catch (err) {
        console.error("Failed to load roles:", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await api.getAllRoleSalaries();
        const list = Array.isArray(res) ? res : res?.data || [];
        const found = list.find((x) => x._id === id);
        if (found) {
          setRoleId(found.roleId?._id || found.roleId || "");
          setDesignation(found.designation || "");
          setFixedSalary(String(found.fixedSalary ?? ""));
          setEffectiveFrom(found.effectiveFrom || "");
          setActive(found.active !== false);
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load");
      }
    })();
  }, [id, isPortal, api]);

  const handleRoleChange = (val) => {
    setRoleId(val);
    const r = roles.find((x) => x._id === val);
    // FIXED: using r.roleName instead of r.name
    if (r && !designation) setDesignation(r.roleName || "");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // NOTE: the Role field used to be a native <select required>, which gave
    // free browser validation. Swapping it for the custom <Select> (needed
    // for consistent styling) means that validation no longer fires on its
    // own, so it's re-implemented here to keep the same "can't submit
    // without a role" behavior.
    if (!roleId) {
      toast.error("Role is required");
      return;
    }
    setLoading(true);
    try {
      await api.upsertRoleSalary({
        roleId,
        designation,
        fixedSalary: Number(fixedSalary),
        effectiveFrom: effectiveFrom || null,
        active,
      });
      navigate(base);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const roleOptions = [
    { label: "Select role", value: "" },
    ...roles.map((r) => ({ label: r.roleName, value: r._id })),
  ];

  return (
    <div className="mx-auto max-w-xl space-y-6 p-4 sm:p-6">
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          <Briefcase className="h-6 w-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          {id ? "Edit" : "Add"} Role Salary
        </h1>
      </div>

      

      <Card>
        <CardContent className="p-6">
          <form onSubmit={onSubmit} className="space-y-4">
            <Select
              label="Role"
              options={roleOptions}
              value={roleId}
              onChange={handleRoleChange}
            />

            <Input
              label="Designation label"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
            />

            <Input
              type="number"
              min="0"
              step="0.01"
              label="Fixed monthly salary"
              value={fixedSalary}
              onChange={(e) => setFixedSalary(e.target.value)}
              required
            />

            <Input
              type="date"
              label="Effective from (optional)"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />

            <Checkbox label="Active" checked={active} onChange={(e) => setActive(e.target.checked)} />

            <div className="flex gap-3 pt-2">
              <Button type="submit" variant="primary" isLoading={loading} leftIcon={<Save className="h-4 w-4" />}>
                Save
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate(base)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}